"use client";

import * as React from "react";
import { X } from "lucide-react";

import { Badge } from "./badge"
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import { Command as CommandPrimitive } from "cmdk";
import lodash from 'lodash'

export type Option = Record<"value" | "label", string>;

type Props = {
    selectedValue?: Option[] | string[];
    onValueChange?: (value?: Option[]) => void;
    options?: Option[];
    placeholder?: string;
    disabled?: boolean;
}

export function MultiSelect({selectedValue, onValueChange, options, placeholder='Select...', disabled=false}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Option[]>([]);
  const [inputValue, setInputValue] = React.useState("");
  
  const initialRender = React.useRef(true)

  React.useEffect(()=>{
    
    const isEqual = lodash.isEqual(selected, selectedValue)
    if(isEqual){ return }

    if(selectedValue && typeof selectedValue[0] === 'string'){
        const newSelected = selectedValue.map(sv=>options?.find(o=>o.value===sv)).filter(o=>o) as Option[]
        setSelected(newSelected)
    } else {
        setSelected(selectedValue as Option[] || [])
    }

    
  }, [selectedValue])

  React.useEffect(()=>{
    if(initialRender.current){
      initialRender.current = false
      return
    }
    onValueChange&&onValueChange(selected)
  }, [selected])

  const handleUnselect = React.useCallback((framework: Option) => {
    setSelected((prev) => prev.filter((s) => s.value !== framework.value));
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === "Delete" || e.key === "Backspace") {
          if (input.value === "") {
            setSelected((prev) => {
              const newSelected = [...prev];
              newSelected.pop();
              return newSelected;
            });
          }
        }
        // This is not a default behaviour of the <input /> field
        if (e.key === "Escape") {
          input.blur();
        }
      }
    },
    []
  );

  const selectables = options?.filter(
    (framework) => !selected.find(i=>i.value===framework.value)
  );

  return (
    <Command
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
    >
      <div className="px-3 py-2 text-sm border rounded-md group border-input ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <div className="flex flex-wrap gap-1">
          {selected.map((framework, index) => {
            return (
              <Badge key={`${framework.label}-${framework.value}-${index}`} variant="secondary">
                {framework.label}
                <button
                  className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUnselect(framework);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(framework)}
                >
                  <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            );
          })}
          {/* Avoid having the "Search" Icon */}
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 ml-2 bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>
      <div className="relative mt-2">
        <CommandList>
          {open && (Array.isArray(selectables) && selectables.length > 0) ? (
            <div className="absolute top-0 z-10 w-full border rounded-md shadow-md outline-none bg-popover text-popover-foreground animate-in">
              <CommandGroup className="h-full overflow-auto">
                {selectables.map((framework, index) => {
                  return (
                    <CommandItem
                      key={`list-${framework.label}-${framework.value}-${index}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onSelect={(value) => {
                        setInputValue("");
                        setSelected((prev) => [...prev, framework]);
                      }}
                      className={"cursor-pointer"}
                    >
                      {framework.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </div>
          ) : null}
        </CommandList>
      </div>
    </Command>
  );
}
